import { buildPageSchemaGraph } from "@/lib/organization";

interface SEOProps {
  title: string;
  description: string;
  path: string;
  h1?: string;
  schema?: object;
  noindex?: boolean;
}

/**
 * Renders per-page head tags using React 19's native metadata hoisting:
 * <title>, <meta>, and <link> rendered anywhere are hoisted into <head>
 * on the client, and to the front of the HTML stream during server
 * rendering (extracted into <head> by scripts/prerender.mjs).
 * The JSON-LD script stays in the body, which search engines accept.
 */
export function SEO({ title, description, path, h1, schema, noindex }: SEOProps) {
  const canonicalUrl = `https://tlcassociates.llc${path === '/' ? '' : path}`;

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      {noindex && <meta name="robots" content="noindex,follow" />}
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content="https://tlcassociates.llc/og-image.jpg" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content="https://tlcassociates.llc/og-image.jpg" />
      {schema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              path === "/privacy" || path === "/gbp-cover"
                ? schema
                : buildPageSchemaGraph(path, title, description, schema, h1),
            ),
          }}
        />
      )}
    </>
  );
}
